import { useQuery } from "@tanstack/react-query";

import { useDataContext } from "./DataProvider";
import { useFetchCMSData } from "./useFetchCMSData";
import { CMS_DATA_SOURCES, CMS_DATASOURCES_EMAIL } from "./static";
import { useCmeContext } from "../fe-cme/CMEProvider";
import type { iDealer, iUserEmailPayload } from "./types";

const CACHE_TIME = 10 * 60 * 1000; // 10 minutes in ms

const useUserConfigPayloads = () => {
  const { fetchData } = useFetchCMSData();
  const {
    siloManifest,
    cmeSiloUiData,
    vehicleData,
    dealershipData,
    dealerData,
    materialCode,
  } = useDataContext();
  const { cmeOptions, getCurrentUserConfig } = useCmeContext();

  const getEdmConfig = (cmeOptions: any) => {
    const config = {} as any;
    const edmConfig = {} as any;

    for (const [key, value] of cmeOptions.selectedOptions) {
      config[key] = value;
      const options = cmeOptions?.optionsToDisplay.get(key)?.options;
      const option = options?.find((option: any) => option.name === value);

      if (option) {
        edmConfig[key] = option.title;
      }
    }
    return edmConfig;
  };

  // *************************
  // Fetch email assets
  // *************************
  const path = siloManifest?.[CMS_DATA_SOURCES]?.get(CMS_DATASOURCES_EMAIL);

  const getedmAssetsDataSource = async (): Promise<any> => {
    const raw = await fetchData(path);
    return raw?.data.assets;
  };

  const { data: edmAssets } = useQuery({
    queryKey: ["edmAssets", path],
    queryFn: () => getedmAssetsDataSource(),
    enabled: Boolean(path),
    staleTime: CACHE_TIME,
  });

  // ********************
  // Payload for updating user config only (ie. sap code, vehicle configuration)
  // ====================
  const getUpdateConfigPayload = (
    config: any,
    sapCode: string,
    dealer: iDealer,
    userData?: iUserEmailPayload,
  ) => ({
    userConfig: {
      vehicle: vehicleData.siloId,
      sapCode,
      dealer,
      config,
      ...(userData && { firstName: userData.firstName }),
    },
  });

  // ********************
  // Payload for adding user config (full email payload)
  // ====================
  const getAddConfigPayload = (userData: iUserEmailPayload) => {
    const { firstName, lastName, email, phone } = userData;
    const { configurator_animation, dealer_placeholder_image, rcr_image_crop_token } =
      edmAssets;
    const model = cmeSiloUiData?.model?.title ?? vehicleData.vehicleId;
    const dealerName = `${dealerData.firstName} ${dealerData.lastName}`;
    const dealerEmail = dealerData.email;
    const dealershipName = dealershipData.name;
    const Date_entered = new Date().toISOString();
    const config = getCurrentUserConfig();

    return {
      sendEmail: true,
      edmAssets: {
        configuratorAnimationURL: configurator_animation,
        dealerImageURL: dealer_placeholder_image,
        rcrImageCropToken: rcr_image_crop_token,
      },
      edmData: {
        firstName,
        lastName,
        email,
        phone,
        model,
        materialCode: materialCode.config,
        dealerName,
        dealerEmail,
        dealershipName,
        Date_entered,
        ...getEdmConfig(cmeOptions),
      },
      ...getUpdateConfigPayload(config, dealershipData.sapCode, dealerData, userData),
    };
  };

  return {
    getUpdateConfigPayload,
    getAddConfigPayload,
  };
};

export default useUserConfigPayloads;
